import { anthropic } from '@ai-sdk/anthropic';
import { deepseek } from '@ai-sdk/deepseek';
import { google } from '@ai-sdk/google';
import { groq } from '@ai-sdk/groq';
import { openai } from '@ai-sdk/openai';
import { perplexity } from '@ai-sdk/perplexity';
import { createOpenRouter } from '@openrouter/ai-sdk-provider';

import {
  type LanguageModelV1,
  extractReasoningMiddleware,
  wrapLanguageModel,
} from 'ai';
import {
  AnthropicIcon,
  DeepSeekIcon,
  GeminiIcon,
  GroqIcon,
  MistralIcon,
  OpenAiIcon,
  PerplexityIcon,
  XaiIcon,
} from '../icons';

export type PriceBracket = 'lowest' | 'low' | 'high' | 'highest';

const MILLION = 1000000;
const THOUSAND = 1000;

const openrouter = createOpenRouter({
  apiKey: process.env.OPENROUTER_API_KEY,
});

// HOC to create cost calculation function
const createCostCalculator = (inputPrice: number, outputPrice: number) => {
  return (tokens: { inputToken: number; outputToken: number }) => {
    const inputCost = (tokens.inputToken / MILLION) * inputPrice;
    const outputCost = (tokens.outputToken / MILLION) * outputPrice;
    return inputCost + outputCost;
  };
};

// Median input cost: 2.7
export const textModels: {
  label: string;
  models: {
    icon: typeof OpenAiIcon;
    id: string;
    label: string;
    model: LanguageModelV1;
    getCost: (tokens: { inputToken: number; outputToken: number }) => number;
    reasoning?: boolean;
    legacy?: boolean;
    priceIndicator?: PriceBracket;
    disabled?: boolean;
    default?: boolean;
  }[];
}[] = [
  {
    label: 'OpenAI',
    models: [
      {
        icon: OpenAiIcon,
        id: 'openai/gpt-4.1',
        label: 'GPT-4.1',
        model: openai('gpt-4.1'),
        priceIndicator: 'high',
        reasoning: false,
        getCost: createCostCalculator(2, 8),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/gpt-4.1-mini',
        label: 'GPT-4.1 Mini',
        model: openai('gpt-4.1-mini'),
        default: true,
        priceIndicator: 'lowest',
        reasoning: false,
        getCost: createCostCalculator(0.4, 1.6),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/gpt-4.1-nano',
        label: 'GPT-4.1 Nano',
        model: openai('gpt-4.1-nano'),
        priceIndicator: 'lowest',
        reasoning: false,
        getCost: createCostCalculator(0.1, 0.4),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/o1',
        label: 'O1',
        model: openai('o1'),
        priceIndicator: 'highest',
        reasoning: true,
        getCost: createCostCalculator(15, 60),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/o3',
        label: 'O3',
        model: openai('o3'),
        priceIndicator: 'highest',
        reasoning: true,
        getCost: createCostCalculator(20, 80),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/o3-mini',
        label: 'O3 Mini',
        model: openai('o3-mini'),
        priceIndicator: 'low',
        reasoning: true,
        getCost: createCostCalculator(1.1, 4.4),
      },
      {
        icon: OpenAiIcon,
        id: 'openai/o4-mini',
        label: 'O4 Mini',
        model: openai('o4-mini'),
        priceIndicator: 'low',
        reasoning: true,
        getCost: createCostCalculator(1.1, 4.4),
      },
    ],
  },
  {
    label: 'xAI',
    models: [
      {
        icon: XaiIcon,
        id: 'openrouter/x-ai/grok-3-beta',
        label: 'Grok 3',
        model: wrapLanguageModel({
          model: openrouter('x-ai/grok-3-beta'),
          middleware: extractReasoningMiddleware({ tagName: 'think' }),
        }),
        priceIndicator: 'highest',
        getCost: createCostCalculator(3, 15),
      },
      {
        icon: XaiIcon,
        id: 'openrouter/x-ai/grok-3-mini-beta',
        label: 'Grok 3 Mini',
        model: wrapLanguageModel({
          model: openrouter('x-ai/grok-3-mini-beta'),
          middleware: extractReasoningMiddleware({ tagName: 'think' }),
        }),
        priceIndicator: 'lowest',
        getCost: createCostCalculator(0.3, 0.5),
      },
    ],
  },

  {
    label: 'Anthropic',
    models: [
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-opus-4-0',
        label: 'Claude Opus 4',
        model: anthropic('claude-opus-4-0'),
        priceIndicator: 'highest',
        reasoning: true,
        getCost: createCostCalculator(15, 75),
      },
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-sonnet-4-0',
        label: 'Claude Sonnet 4',
        model: anthropic('claude-sonnet-4-0'),
        reasoning: true,
        getCost: createCostCalculator(3, 15),
      },
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-3-7-sonnet-latest',
        label: 'Claude Sonnet 3.7',
        reasoning: true,
        model: anthropic('claude-3-7-sonnet-latest'),
        getCost: createCostCalculator(3, 15),
      },
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-3-5-sonnet-latest',
        label: 'Claude Sonnet 3.5',
        reasoning: false,
        model: anthropic('claude-3-5-sonnet-latest'),
        getCost: createCostCalculator(3, 15),
        legacy: true,
      },
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-3-haiku-latest',
        label: 'Claude Haiku 3.5',
        reasoning: false,
        model: anthropic('claude-3-5-haiku-latest'),
        priceIndicator: 'lowest',
        getCost: createCostCalculator(0.8, 4),
      },
      {
        icon: AnthropicIcon,
        id: 'anthropic/claude-3-opus-latest',
        label: 'Claude Opus 3',
        reasoning: false,
        model: anthropic('claude-3-opus-latest'),
        priceIndicator: 'highest',
        getCost: createCostCalculator(15, 75),
      },
    ],
  },

  {
    label: 'Mistral',
    models: [
      {
        icon: MistralIcon,
        id: 'openrouter/mistralai/magistral-medium-2506',
        label: 'Magistral Medium',
        model: wrapLanguageModel({
          model: openrouter('mistralai/magistral-medium-2506'),
          middleware: extractReasoningMiddleware({ tagName: 'think' }),
        }),
        reasoning: true,
        priceIndicator: 'high',
        getCost: createCostCalculator(2, 5),
      },
      {
        icon: MistralIcon,
        id: 'openrouter/mistralai/magistral-small-2506',
        label: 'Magistral Small',
        model: wrapLanguageModel({
          model: openrouter('mistralai/magistral-small-2506'),
          middleware: extractReasoningMiddleware({ tagName: 'think' }),
        }),
        reasoning: true,
        getCost: createCostCalculator(0.5, 1.5),
      },
      {
        icon: MistralIcon,
        id: 'openrouter/mistralai/mistral-medium-3',
        label: 'Mistral Medium 3',
        model: openrouter('mistralai/mistral-medium-3'),
        getCost: createCostCalculator(0.4, 2),
      },
      {
        icon: MistralIcon,
        id: 'openrouter/mistralai/mistral-small-3.1-24b-instruct',
        label: 'Mistral Small 3.1',
        model: openrouter('mistralai/mistral-small-3.1-24b-instruct'),
        priceIndicator: 'lowest',
        getCost: createCostCalculator(0.1, 0.3),
      },
    ],
  },

  {
    label: 'Google',
    models: [
      {
        icon: GeminiIcon,
        id: 'google/gemini-2.5-pro-preview',
        label: 'Gemini 2.5 Pro Preview',
        model: google('gemini-2.5-pro-preview-05-06'),
        priceIndicator: 'highest',
        reasoning: true,
        getCost: createCostCalculator(2.5, 15),
      },
      {
        icon: GeminiIcon,
        id: 'google/gemini-1.5-flash',
        label: 'Gemini 2.5 Flash Preview',
        model: google('gemini-2.5-flash-preview-04-17'),
        priceIndicator: 'lowest',
        reasoning: true,
        getCost: createCostCalculator(0.15, 0.6),
      },
    ],
  },

  {
    label: 'DeepSeek',
    models: [
      {
        icon: DeepSeekIcon,
        id: 'deepseek-deepseek-chat',
        label: 'DeepSeek V3',
        model: deepseek('deepseek-chat'),
        priceIndicator: 'lowest',
        reasoning: false,
        getCost: createCostCalculator(0.27, 1.1),
      },
      {
        icon: DeepSeekIcon,
        id: 'deepseek-deepseek-reasoner',
        label: 'DeepSeek R1',
        model: deepseek('deepseek-reasoner'),
        priceIndicator: 'low',
        reasoning: true,
        getCost: createCostCalculator(0.55, 2.19),
      },
    ],
  },

  // {
  //   label: 'Cerebras',
  //   models: [
  //     {
  //       icon: CerebrasIcon,
  //       id: 'cerebras-llama3.1-8b',
  //       label: 'Llama 3.1 8B',
  //       model: cerebras('llama3.1-8b'),
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 0.1;
  //         const outputCost = (output / million) * 0.1;

  //         return inputCost + outputCost;
  //       },
  //     },
  //     {
  //       icon: CerebrasIcon,
  //       id: 'cerebras-llama3.3-70b',
  //       label: 'Llama 3.3 70B',
  //       model: cerebras('llama3.3-70b'),
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 0.85;
  //         const outputCost = (output / million) * 1.2;

  //         return inputCost + outputCost;
  //       },
  //     },
  //   ],
  // },

  {
    label: 'Groq',
    models: [
      {
        icon: GroqIcon,
        id: 'groq-meta-llama/llama-4-scout-17b-16e-instruct',
        label: 'Llama 4 Scout 17B',
        model: groq('meta-llama/llama-4-scout-17b-16e-instruct'),
        priceIndicator: 'lowest',
        getCost: createCostCalculator(0.11, 0.34),
      },
      {
        icon: GroqIcon,
        id: 'groq-gemma2-9b-it',
        label: 'Gemma 2 9B',
        model: groq('gemma2-9b-it'),
        priceIndicator: 'lowest',
        getCost: createCostCalculator(0.2, 0.2),
      },
      {
        icon: GroqIcon,
        id: 'groq-deepseek-r1-distill-llama-70b',
        label: 'DeepSeek R1 Distill Llama 70B',
        model: wrapLanguageModel({
          model: groq('deepseek-r1-distill-llama-70b'),
          middleware: extractReasoningMiddleware({ tagName: 'think' }),
        }),
        priceIndicator: 'lowest',
        reasoning: true,
        getCost: createCostCalculator(0.75, 0.99),
      },
      {
        icon: GroqIcon,
        id: 'groq/qwen/qwen3-32b',
        label: 'Qwen 3 32B',
        model: groq('qwen/qwen3-32b'),
        priceIndicator: 'lowest',
        reasoning: true,
        getCost: createCostCalculator(0.29, 0.39),
      },
    ],
  },
  {
    label: 'Perplexity',
    models: [
      {
        icon: PerplexityIcon,
        id: 'perplexity/sonar',
        label: 'Sonar',
        model: perplexity('sonar'),

        getCost: (tokens: { inputToken: number; outputToken: number }) => {
          const inputCost = (tokens.inputToken / MILLION) * 1;
          const outputCost = (tokens.outputToken / MILLION) * 1;
          const pricePerRequest = 12 / THOUSAND;

          return inputCost + outputCost + pricePerRequest;
        },
      },
      {
        icon: PerplexityIcon,
        id: 'perplexity/sonar-pro',
        label: 'Sonar Pro',
        model: perplexity('sonar-pro'),
        priceIndicator: 'highest',
        getCost: (tokens: { inputToken: number; outputToken: number }) => {
          const inputCost = (tokens.inputToken / MILLION) * 3;
          const outputCost = (tokens.outputToken / MILLION) * 15;
          const pricePerRequest = 14 / THOUSAND;

          return inputCost + outputCost + pricePerRequest;
        },
      },
    ],
  },
  // {
  //   label: 'Cohere',
  //   models: [
  //     {
  //       icon: CohereIcon,
  //       id: 'cohere-command-a-03-2025',
  //       label: 'Command A',
  //       model: cohere('command-a-03-2025'),
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 2.5;
  //         const outputCost = (output / million) * 10;

  //         return inputCost + outputCost;
  //       },
  //     },
  //     {
  //       icon: CohereIcon,
  //       id: 'cohere-command-r',
  //       label: 'Command R',
  //       model: cohere('command-r'),
  //       priceIndicator: 'lowest',
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 0.15;
  //         const outputCost = (output / million) * 0.6;

  //         return inputCost + outputCost;
  //       },
  //     },
  //     {
  //       icon: CohereIcon,
  //       id: 'cohere-command-r-plus',
  //       label: 'Command R Plus',
  //       model: cohere('command-r-plus'),
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 2.5;
  //         const outputCost = (output / million) * 10;

  //         return inputCost + outputCost;
  //       },
  //     },
  //     {
  //       icon: CohereIcon,
  //       id: 'cohere-command-r7b-12-2024',
  //       label: 'Command R7B',
  //       model: cohere('command-r7b-12-2024'),
  //       priceIndicator: 'lowest',
  //       getCost: ({ input, output }: { input: number; output: number }) => {
  //         const inputCost = (input / million) * 0.0375;
  //         const outputCost = (output / million) * 0.15;

  //         return inputCost + outputCost;
  //       },
  //     },
  //   ],
  // },
];
